import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Divider, Form, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DatePicker, DealersAndLocationsDropdown } from "components";
import { usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import "modules/NewsDashboard/components/ArticleModal/ArticleModal.scss";
import { useToggleArticleDisableMutation } from "modules/NewsDashboard/components/ArticleModal/hooks";
import { LANGUAGE_OPTIONS, useNewsCategoriesQuery } from "modules/NewsDashboard/hooks";
import { isAdmin } from "utils/common";

import type { Article } from "models";

export type CreateArticleFormValues = Pick<
  Article,
  "title" | "category_id" | "publish_date" | "expire_date" | "content" | "selector" | "visible_on_tablet" | "visible_to_all"
> & {
  language_code: string;
  dealer_ids: number[];
  location_ids: number[];
};

type ArticleFormProps = {
  article: Article | null;
  isPending: boolean;
  hasValidationErrors: boolean;
  onClose: () => void;
  refetchArticles: () => void;
};

const FormHelper = createFormHelper<CreateArticleFormValues>();

export const ArticleForm = ({ onClose, isPending, hasValidationErrors, article, refetchArticles }: ArticleFormProps) => {
  const { t } = useTranslation();

  const { watch, setValue } = FormHelper.useFormContext();
  const { data: categories } = useNewsCategoriesQuery();
  const { toggleDisableMutation, isPending: isToggling } = useToggleArticleDisableMutation();
  const { dealers } = usePermanentRealtimeDealers();
  const { user } = usePermanentRealTimeUser();

  const [dealerIds, locationIds, title, selector, content, visibleToAll, publishDate, expireDate] = watch([
    "dealer_ids",
    "location_ids",
    "title",
    "selector",
    "content",
    "visible_to_all",
    "publish_date",
    "expire_date"
  ]);

  const publishDateValue = publishDate ? new Date(publishDate.split("T")[0]) : null;
  const expireDateValue = expireDate ? new Date(expireDate.split("T")[0]) : null;

  const languageOptions = Object.entries(LANGUAGE_OPTIONS)
    .filter(([_key, value]) => value !== LANGUAGE_OPTIONS.All)
    .map(([key, value]) => ({
      key: value,
      value: value,
      text: t(`v8_${key.toLowerCase()}`) || key
    }));

  const categoryOptions = categories?.map(cat => ({ key: cat.id, value: cat.id, text: t(`v8_${cat.name.toLowerCase()}`) || cat.name })) || [];

  const handleDealerLocationChange = ({ dealerIds: dealer_ids, locationIds: location_ids }: { dealerIds: number[]; locationIds: number[] }) => {
    const visibleToAll = dealers.length > 0 && dealer_ids.length === dealers.length && !location_ids.length;
    setValue("visible_to_all", visibleToAll);
    setValue("dealer_ids", visibleToAll ? [] : dealer_ids);
    setValue("location_ids", visibleToAll ? [] : location_ids);
  };

  const handleChangePublishDate = (date: Date | null) => {
    const newPublishDate = date ? format(date, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : null;
    setValue("publish_date", newPublishDate);

    if (newPublishDate && expireDate && new Date(newPublishDate) > new Date(expireDate)) setValue("expire_date", null);
  };

  const handleChangeExpireDate = (date: Date | null) => {
    const newExpireDate = date ? format(date, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : null;
    setValue("expire_date", newExpireDate);

    if (newExpireDate && publishDate && new Date(newExpireDate) < new Date(publishDate)) setValue("publish_date", null);
  };

  const handleToggleDisable = async () => {
    if (!article) return;
    await toggleDisableMutation(article);
    onClose();
    refetchArticles();
  };

  const isEdit = Boolean(article);

  return (
    <div>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{isEdit ? t("v8_edit_article", "Edit Article") : t("v8_add_article", "Add Article")}</div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        {isAdmin(user?.role_id ?? 0) && (
          <Form.Group>
            <Form.Field error={!selector && hasValidationErrors}>
              <FormHelper.Field.Input name="selector" inputProps={{ placeholder: t("v8_selector", "Selector"), fluid: true }} />
            </Form.Field>
          </Form.Group>
        )}

        <Form.Group>
          <Form.Field required error={!title && hasValidationErrors}>
            <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_title", "Title"), fluid: true }} />
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field error={!locationIds.length && !dealerIds.length && !visibleToAll && hasValidationErrors}>
            <label>{t("v8_dealers_locations", "Dealers / Locations")}</label>
            <DealersAndLocationsDropdown values={{ dealerIds, locationIds }} handleOnChange={handleDealerLocationChange} allowSelectAllDealers />
          </Form.Field>

          <Form.Field>
            <label>{t("v8_category", "Category")}</label>
            <FormHelper.Field.Dropdown
              name="category_id"
              options={categoryOptions}
              dropdownProps={{ placeholder: t("v8_category", "Category"), fluid: true, clearable: true }}
            />
          </Form.Field>

          <Form.Field>
            <label>{t("v8_language", "Language")}</label>
            <FormHelper.Field.Dropdown name="language_code" options={languageOptions} dropdownProps={{ placeholder: t("v8_language", "Language"), fluid: true }} />
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field>
            <label>{t("v8_publish_date", "Publish Date")}</label>
            <DatePicker fluid date={publishDateValue} onDateChange={handleChangePublishDate} showLabel />
          </Form.Field>

          <Form.Field>
            <label>{t("v8_expiry_date", "Expiry Date")}</label>
            <DatePicker fluid date={expireDateValue} onDateChange={handleChangeExpireDate} showLabel />
          </Form.Field>

          <Form.Field>
            <div style={{ paddingTop: "25px" }}>
              <FormHelper.Field.Checkbox name="visible_on_tablet" label={t("v8_visible_on_tablet", "Visible on tablet")} />
            </div>
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field>
            <ReactQuill
              onChange={(value: string) => setValue("content", value)}
              value={content}
              theme="snow"
              style={{ height: "200px", marginBottom: "30px" }}
              className={!content && hasValidationErrors ? "quill-error" : ""}
              modules={{
                toolbar: [["bold", "italic", "underline", "link", "image", "video"]]
              }}
            />
          </Form.Field>
        </Form.Group>
      </Modal.Content>

      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          <div>
            {isEdit && (
              <Form.Button
                type="button"
                className={`toggle-disable-button ${article?.disabled ? "enable" : "disable"}`}
                disabled={isPending || isToggling}
                onClick={handleToggleDisable}
              >
                <Icon className="circle exclamation solid" />
                {article?.disabled ? t("v8_enable", "Enable") : t("v8_disable", "Disable")}
              </Form.Button>
            )}
          </div>

          <div className="right-buttons">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>

            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </div>
  );
};
